import {
  BSC_CHAIN_ID,
  SALE_CHAIN_DECIMALS,
  SALE_TOKEN_UNIT,
} from "./sale-flow.constants";
import {
  getSupportedNetworkChainIds,
  isSupportedNetworkChainId,
  normalizeChainId,
} from "app/utils/network-chain.utils";

export { normalizeChainId };

export const getSupportedChainIds = (
  supportedNetworks: Parameters<typeof getSupportedNetworkChainIds>[0] = []
): number[] => {
  return getSupportedNetworkChainIds(supportedNetworks);
};

export const isSupportedChainId = (
  chainId: number | null,
  supportedChainIds: number[]
): chainId is number => {
  return isSupportedNetworkChainId(chainId, supportedChainIds);
};

export const getStablecoinDecimals = (chainId: number): number => {
  return chainId === BSC_CHAIN_ID
    ? SALE_CHAIN_DECIMALS.bscMainnet
    : SALE_CHAIN_DECIMALS.defaultStablecoin;
};

type EffectiveMinAllocationParams = {
  dbMinAllocationUsd: number;
  contractMinPurchaseAmount?: bigint | null;
  tokenPriceUsd: number;
};

/**
 * Sale tokens received for a USD amount, as an exact decimal string with at most 6
 * decimals, rounded DOWN.
 *
 * Plain float division is wrong here: 0.35 / 0.0035 is 99.99999999999999, which is below
 * a contract minimum of exactly 100 tokens and makes the purchase revert. Rounding down
 * (never up) also keeps the on-chain payment from exceeding the amount the user entered
 * and approved. The contract prices in micro-USD, so integer math on micro units is exact.
 */
export const computeTokensToReceive = (
  amountUsd: string | number,
  tokenPriceUsd: number
): string => {
  const raw =
    typeof amountUsd === "number"
      ? amountUsd.toFixed(6)
      : String(amountUsd).trim();
  if (!/^\d*\.?\d*$/.test(raw) || raw === "" || raw === ".") return "";

  const [whole = "", fraction = ""] = raw.split(".");
  const amountMicro =
    BigInt(whole || "0") * BigInt(1e6) +
    BigInt(fraction.padEnd(6, "0").slice(0, 6));
  const priceMicro = BigInt(Math.round(tokenPriceUsd * 1e6));
  if (amountMicro <= BigInt(0) || priceMicro <= BigInt(0)) return "";

  const tokensMicro = (amountMicro * BigInt(1e6)) / priceMicro;
  const integer = tokensMicro / BigInt(1e6);
  const decimals = (tokensMicro % BigInt(1e6))
    .toString()
    .padStart(6, "0")
    .replace(/0+$/, "");
  return decimals ? `${integer}.${decimals}` : integer.toString();
};

export const getEffectiveMinAllocationUsd = ({
  dbMinAllocationUsd,
  contractMinPurchaseAmount,
  tokenPriceUsd,
}: EffectiveMinAllocationParams): number => {
  if (!contractMinPurchaseAmount || tokenPriceUsd <= 0) {
    return dbMinAllocationUsd;
  }

  const contractMinTokens = Number(contractMinPurchaseAmount) / SALE_TOKEN_UNIT;

  if (!Number.isFinite(contractMinTokens)) {
    return dbMinAllocationUsd;
  }

  // Round to stablecoin precision: 35 tokens * 0.01 is 0.35000000000000003 in floating
  // point, which would reject an exact 0.35 input and print the noise in the range message.
  const contractMinUsd =
    Math.round(contractMinTokens * tokenPriceUsd * 1e6) / 1e6;

  return Math.max(dbMinAllocationUsd, contractMinUsd);
};

const EXPLORER_TX_BASE: Record<number, string> = {
  1: "https://etherscan.io/tx/",
  56: "https://bscscan.com/tx/",
  137: "https://polygonscan.com/tx/",
  8453: "https://basescan.org/tx/",
  42161: "https://arbiscan.io/tx/",
  59144: "https://lineascan.build/tx/",
  4663: "https://robin.etherscan.io/tx/",
};

export const getExplorerTxUrl = (
  chainId?: number | null,
  hash?: string | null
): string | null => {
  if (!chainId || !hash) return null;
  const base = EXPLORER_TX_BASE[chainId];
  return base ? `${base}${hash}` : null;
};

// Message for a transaction that was mined but reverted on-chain. wagmi's
// useWaitForTransactionReceipt resolves successfully for reverted txs, so we
// must surface the real reason instead of a generic "not found".
export const buildSaleRevertedError = (
  hash?: string | null,
  chainId?: number | null
): string => {
  const url = getExplorerTxUrl(chainId, hash);
  return `Your purchase transaction failed (reverted on-chain) — most likely the wallet didn't have enough balance to cover the amount. No funds were taken, only the network fee.${
    url ? ` View it: ${url}` : ""
  }`;
};

// Message for an approve transaction that was mined but reverted on-chain. The
// purchase is not attempted after it.
export const buildApproveRevertedError = (
  hash?: string | null,
  chainId?: number | null
): string => {
  const url = getExplorerTxUrl(chainId, hash);
  return `Your token approval failed (reverted on-chain), so the purchase was not sent. No funds were taken, only the network fee. Please try again.${
    url ? ` View it: ${url}` : ""
  }`;
};

// Message for when the receipt itself could not be read (RPC/timeout), as
// opposed to a tx that does not exist.
export const buildSaleReceiptError = (
  hash?: string | null,
  chainId?: number | null
): string => {
  const url = getExplorerTxUrl(chainId, hash);
  return url
    ? `We couldn't confirm your transaction on the network. Please check its status on the explorer: ${url}`
    : "We couldn't confirm your transaction on the network. Please check your wallet and try again.";
};
